import moment, { Moment } from 'moment'
import React, { useCallback, useState, useContext } from 'react'
import { useIntl } from 'react-intl'

import DateRangePicker from 'tripPlanner/Tripkit/Common/DateRangePicker'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { ModalElementContext } from 'contexts/ModalProvider'
import { Locations } from 'tripPlanner/pages/trip/GenerateTripPage'

interface Props {
  initStartDate?: Moment
  initEndDate?: Moment
  onSetDates: (startDate?: Moment, endDate?: Moment) => void
  locations?: Array<Locations>
}

function AITripDatePicker(props: Props) {
  const intl = useIntl()
  const {
    initStartDate,
    initEndDate,
    onSetDates,
    locations,
  } = props

  const modalElement = useContext(ModalElementContext)
  const closeModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const [startDate, setStartDate] = useState<moment.Moment | undefined>(
    initStartDate,
  )
  const [endDate, setEndDate] = useState<moment.Moment | undefined>(initEndDate)

  const onDatesChange = useCallback(
    (dates: { startDate: moment.Moment, endDate?: moment.Moment }) => {
      setStartDate(dates.startDate)
      setEndDate(dates.endDate)

      if (dates.startDate && dates.endDate && locations?.length === 1) {
        onSetDates(dates.startDate, dates.endDate)
      }
    },
    [setStartDate, setEndDate, onSetDates, locations],
  )

  const onConfirm = useCallback(() => {
    if (startDate && endDate) {
      onSetDates(startDate, endDate)
    }
    closeModal()
  }, [startDate, endDate, onSetDates, closeModal])

  const onCancel = useCallback(() => {
    closeModal()
  }, [closeModal])

  if (locations?.length === 1) {
    return (
      <DateRangePicker
        onDatesChange={onDatesChange}
        startDate={startDate}
        endDate={endDate}
        allowSameDay={false}
        showTripDates
      />
    )
  }

  const modalTitle = intl.formatMessage({
    id: 'BHUbcQpRmK',
    defaultMessage: 'Select Trip Dates',
    description: 'Title for trip date picker modal',
  })

  const confirmButtonText = intl.formatMessage({
    id: '/MzVM78ce/',
    defaultMessage: 'Confirm',
    description: 'Button text to confirm selected dates',
  })

  const cancelButtonText = intl.formatMessage({
    id: 'ugwn4zTPLm',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel date selection',
  })

  return (
    <ModalBase size="S">
      <ModalHeader title={modalTitle} onCloseButtonClick={onCancel} />
      <ModalBody>
        <ModalContent>
          <DateRangePicker
            onDatesChange={onDatesChange}
            startDate={startDate}
            endDate={endDate}
            allowSameDay={false}
            showTripDates
          />
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: confirmButtonText,
          disabled: !startDate || !endDate,
          onClick: onConfirm,
        }}
        secondaryActionProps={{
          children: cancelButtonText,
          onClick: onCancel,
        }}
      />
    </ModalBase>
  )
}

export default AITripDatePicker
